import React from "react";
//#region types
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { StyleEnums, DialogEnums, AuthEnums } from "models/enums";
import { MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { QuotationMoreActionProps } from "models/propTypes/quotationPropTypes";
//#endregion
//#region components
import { MoreActions } from "components/common";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuotesService } from "serviceHooks";
//#endregion
import { CompanyService } from "services";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const QuotationMoreActions: React.FC<QuotationMoreActionProps> = (props) => {
  const quotesService = useQuotesService();
  const location = useLocation();
  const navigate = useNavigate();

  const cancelQuotation = () => {
    return quotesService.deleteQuotation(props.quotation.serialNumber);
  };

  const onQuotationCancel = () => {
    navigate(UrlHelper.formatPath(`/Reports/Quotes`), {
      replace: true,
      state: { background: location },
    });
  };

  const cancelAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
    icon: IconDefinitionConstants.WTimes,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
      titleIcon: IconDefinitionConstants.WTimes,
      dialogType: DialogEnums.DialogType.Message,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
          onClick: cancelQuotation,
          type: StyleEnums.DisplayType.Danger,
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Quotation.CancelSuccessMessage
          ),
        },
      ],
      children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.CancelWarning),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: onQuotationCancel,
  };

  const copyQuotationAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.CopyAction),
    icon: IconDefinitionConstants.WCopy,
    onClickHandler: async () => {
      navigate(
        UrlHelper.formatPath(
          `/Quotation/Create?${RouteConstants.QuotationQueryParams.QuotationTemplate}=${props.quotation.serialNumber}`
        ),
        {
          state: { background: location },
        }
      );
    },
  };

  const editQuotationAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.EditQuotation),
    icon: IconDefinitionConstants.WEdit,
    onClickHandler: async () => {
      navigate(
        UrlHelper.formatPath(
          `/Quotation/${props.quotation.serialNumber}?${RouteConstants.QuotationQueryParams.IsEdit}=true`
        ),
        {
          replace: true,
          state: { background: location },
        }
      );
    },
  };

  const createInvoiceFromQuotationAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.CreateInvoice),
    icon: IconDefinitionConstants.WSend,
    onClickHandler: async () => {
      navigate(
        UrlHelper.formatPath(
          `/Invoice/Create?${RouteConstants.QueryParamStrings.QuotationTemplate}=${props.quotation.serialNumber}`
        ),
        {
          replace: true,
          state: { background: location },
        }
      );
    },
  };

  const onDownloadClick = async () => {
    await quotesService.downloadPdf(props.quotation.serialNumber);
  };

  const downloadAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Download),
    icon: IconDefinitionConstants.WDownload,
    onClickHandler: () => onDownloadClick(),
  };

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const status = props.quotation.quotationState;
    const actions: MoreActionMenuItemType[] = [];
    if (!CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Auditor)) {
      if (status === ApiModel.QuotationApiState.Won) {
        actions.push(createInvoiceFromQuotationAction);
      }
      if (status !== ApiModel.QuotationApiState.NotSent) {
        actions.push(downloadAction);
      }

      actions.push(copyQuotationAction);
      if (status === ApiModel.QuotationApiState.NotSent) {
        actions.push(editQuotationAction);
        actions.push(cancelAction);
      }
    } else if (status !== ApiModel.QuotationApiState.NotSent) {
      actions.push(downloadAction);
    }
    return actions;
  };

  return <MoreActions menuItems={getMenuItems()} />;
};

export default QuotationMoreActions;
